import React, { useState } from 'react';
import NoteList from '../../NoteList';
import { useMutation } from '@apollo/client/react';
import { ADD_JOB_NOTES, EDIT_JOB_NOTES } from '../../../graphql/client/mutations';
import { GET_JOB_NOTES } from '../../../graphql/client/queries';
import { AddJobNotes, EditJobNotes } from '../../../types/schemaTypes';
import { Query } from '../../_common';
import { Error, Loading } from '../../_common';
import { Note } from '../../../interfaces/Notes/note';
import { useAuthContext } from '../../Auth/AuthProvider';
import { PermissionEnum } from '../../../types/PermissionEnum';
import {  toast } from 'react-toastify';
import {toastOpt} from '../../../utils/helpers/toastHelper'
interface JobNotesProps {
    jobId: number;
}

// TODO Check the backend query for this value
// Do not change this value unless the associate backend value has been updated - (5/11/19) - KS
const PAGE_SIZE = 50;
const SuccessMessage = {
    save: 'Notes added successfully',
    update:'Notes updated successfully'
};
const JobNotes = ({ jobId }: JobNotesProps): React.ReactElement | null => {
    const [isEndOfResults, setIsEndOfResults] = useState(false);
    const authContext = useAuthContext();
    const canAddNewNotes = authContext.userHasPermission(PermissionEnum.AddJobNotes);
    const canViewNotesList = authContext.userHasPermission(PermissionEnum.ViewJobNotes);
    const canEditJobNotes = useAuthContext().userHasPermission(PermissionEnum.EditJobNotes);
    const onJobNotesAddorEditFail = errorMessage => {
        const alertMessage = errorMessage[0].includes('Not permitted to update the job notes created by another user') ? 'This note can only be edited by the user who created it. You can add another note instead' : errorMessage[0]; 
        toast.error(alertMessage,toastOpt);
        //refetch();
    };
    const onJobNotesAddorEditSuccess = (action) =>{
        toast.success(SuccessMessage[action],toastOpt);
    }
    const [addJobNotes] = useMutation<AddJobNotes>(ADD_JOB_NOTES, {
        onError: (error) => {console.log("error",error)},
        onCompleted: (data) => { 
            
            if (data.addJobNotes === null) return;
            const addJobNotesError = (data.addJobNotes.errors && data.addJobNotes.errors.length > 0) ? data.addJobNotes.errors : null ;
            if (addJobNotesError){
                
                onJobNotesAddorEditFail(addJobNotesError)
            } else {
                
                onJobNotesAddorEditSuccess('save');
            }
        
        },
        refetchQueries: () => {
            return [
                {
                    query: GET_JOB_NOTES,
                    variables: { jobId: jobId },
                },
            ];
        },
    });
    const [editJobNotes] = useMutation<EditJobNotes>(EDIT_JOB_NOTES, {
        onError: (error) => {console.log("error",error)},
        onCompleted: (data) => { 
            
            if (data.editJobNotes === null) return;
            const editJobNotesError = (data.editJobNotes.errors && data.editJobNotes.errors.length > 0) ? data.editJobNotes.errors : null ;
            if (editJobNotesError){
                
                onJobNotesAddorEditFail(editJobNotesError)
            } else {
                
                onJobNotesAddorEditSuccess('update');
            }
        
        },
        refetchQueries: () => {
            return [
                {
                    query: GET_JOB_NOTES,
                    variables: { jobId: jobId },
                },
            ];
        },
    });

    const onSaveNote = (noteText: string): void => {
        const addJobNoteInput = {
            notes: noteText,
            jobId: jobId,
        };
        addJobNotes({ variables: { input: addJobNoteInput } });
    };

    const onEditNote = (noteText: string, noteId: number): void => {
        const editJobNoteInput = {
            id: noteId,
            newValues: {
                notes: noteText,
            },
        };
        editJobNotes({ variables: { input: editJobNoteInput } });
    };

    return (
        <Query
            query={GET_JOB_NOTES}
            variables={{
                jobId: jobId,
            }}
        >
            {({ loading, error, data, fetchMore }: any): React.ReactElement | null => {

                const offset = data && data.getJobNotes ? data.getJobNotes.length / PAGE_SIZE : 0;                

                React.useEffect(()=> {
                    if (
                        offset % 1 !== 0 ||
                        (!isEndOfResults && data && data.getJobNotes && data.getJobNotes.length < PAGE_SIZE)
                    ) {
                        // If offset is not a whole number, the end of the results has been reached. (5/11/19) - KS
                        setIsEndOfResults(true);
                    }
                },[offset, data]);

                if (loading) {
                    return <Loading />;
                }

                if (error) {
                    return <Error error={error} />;
                }

                if (data && data.getJobNotes) {
                    const notesList: Note[] = [];
                    if (data.getJobNotes.length > 0) {
                        data.getJobNotes.forEach((item: any) => {
                            const note: Note = {
                                noteId: item.notesId,
                                notes: item.notes,
                                createdAtUtc: item.createdAt,
                                modifiedAtUtc: item.modifiedAt,
                                createdUserId: item.createdUserId,
                                entityId: item.jobId,
                                isDeleted: item.isDeleted,
                                name: item.name,
                                userName: item.userName,
                                email: item.email,
                            };
                            notesList.push(note);
                        });
                    }

                    return (
                        <NoteList
                            isLoading={loading}
                            isEndOfResults={isEndOfResults}
                            onLoadMore={() =>
                                fetchMore({
                                    variables: {
                                        offset,
                                    },
                                    updateQuery: (prev: any, { fetchMoreResult }: any) => {
                                        if (
                                            prev &&
                                            fetchMoreResult &&
                                            prev.getJobNotes &&
                                            fetchMoreResult.getJobNotes
                                        ) {
                                            return Object.assign({}, prev, {
                                                getJobNotes: [...prev.getJobNotes, ...fetchMoreResult.getJobNotes],
                                            });
                                        } else {
                                            return prev;
                                        }
                                    },
                                })
                            }
                            noteItems={notesList}
                            onSaveNote={onSaveNote}
                            onEditNote={onEditNote}
                            canViewNotesList={canViewNotesList}
                            canAddNewNotes={canAddNewNotes}
                            canEditNote={canEditJobNotes}
                        />
                    );
                } else {
                    return null;
                }
            }}
        </Query>
    );
};

export default JobNotes;
